import type {
  SupportTicketSearchHighlight,
  SupportTicketSearchHighlightField,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import {
  SUPPORT_TICKET_SEARCH_HIGHLIGHT_POST_TAG,
  SUPPORT_TICKET_SEARCH_HIGHLIGHT_PRE_TAG,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";

export interface SearchHighlightSegment {
  text: string;
  highlighted: boolean;
}

export interface SupportTicketSearchHighlightMatch {
  field: SupportTicketSearchHighlightField;
  fragment: string;
}

interface SearchHighlightPreviewOptions {
  contextAfterCharacters?: number;
  contextBeforeCharacters?: number;
}

const DEFAULT_HIGHLIGHT_CONTEXT_AFTER_CHARACTERS = 96;
const DEFAULT_HIGHLIGHT_CONTEXT_BEFORE_CHARACTERS = 48;

export function pickSupportTicketSearchHighlightMatch(
  searchHighlights: readonly SupportTicketSearchHighlight[] | undefined,
  fields: readonly SupportTicketSearchHighlightField[],
): SupportTicketSearchHighlightMatch | null {
  if (!searchHighlights || searchHighlights.length === 0) {
    return null;
  }

  for (const field of fields) {
    const highlight = searchHighlights.find((entry) => entry.field === field);
    const fragment = highlight?.fragments[0];
    if (fragment) {
      return { field, fragment };
    }
  }

  return null;
}

export function pickSupportTicketSearchHighlight(
  searchHighlights: readonly SupportTicketSearchHighlight[] | undefined,
  fields: readonly SupportTicketSearchHighlightField[],
): string | null {
  return (
    pickSupportTicketSearchHighlightMatch(searchHighlights, fields)?.fragment ??
    null
  );
}

export function parseSupportTicketSearchHighlightFragment(
  fragment: string,
): SearchHighlightSegment[] {
  const segments: SearchHighlightSegment[] = [];
  let cursor = 0;

  while (cursor < fragment.length) {
    const startIndex = fragment.indexOf(
      SUPPORT_TICKET_SEARCH_HIGHLIGHT_PRE_TAG,
      cursor,
    );
    if (startIndex === -1) {
      segments.push({ text: fragment.slice(cursor), highlighted: false });
      break;
    }

    const highlightStart =
      startIndex + SUPPORT_TICKET_SEARCH_HIGHLIGHT_PRE_TAG.length;
    const endIndex = fragment.indexOf(
      SUPPORT_TICKET_SEARCH_HIGHLIGHT_POST_TAG,
      highlightStart,
    );
    if (endIndex === -1) {
      segments.push({ text: fragment.slice(cursor), highlighted: false });
      break;
    }

    if (startIndex > cursor) {
      segments.push({
        text: fragment.slice(cursor, startIndex),
        highlighted: false,
      });
    }

    segments.push({
      text: fragment.slice(highlightStart, endIndex),
      highlighted: true,
    });
    cursor = endIndex + SUPPORT_TICKET_SEARCH_HIGHLIGHT_POST_TAG.length;
  }

  return segments.filter((segment) => segment.text.length > 0);
}

export function buildSupportTicketSearchHighlightPreview(
  fragment: string,
  {
    contextAfterCharacters = DEFAULT_HIGHLIGHT_CONTEXT_AFTER_CHARACTERS,
    contextBeforeCharacters = DEFAULT_HIGHLIGHT_CONTEXT_BEFORE_CHARACTERS,
  }: SearchHighlightPreviewOptions = {},
): SearchHighlightSegment[] {
  const segments = parseSupportTicketSearchHighlightFragment(fragment);
  const firstHighlight = findFirstHighlightBounds(segments);
  if (!firstHighlight) {
    return segments;
  }

  const totalLength = getSegmentsTextLength(segments);
  const start = Math.max(0, firstHighlight.start - contextBeforeCharacters);
  const end = Math.min(
    totalLength,
    firstHighlight.end + contextAfterCharacters,
  );
  const previewStart = expandStartToWordBoundary(segments, start);
  const preview = sliceSearchHighlightSegments(segments, previewStart, end);

  if (previewStart > 0) {
    preview.unshift({ text: "...", highlighted: false });
  }
  if (end < totalLength) {
    preview.push({ text: "...", highlighted: false });
  }

  return preview;
}

export function buildSupportTicketSearchHighlightFragmentFromText(
  text: string,
  searchTerms: readonly string[],
): string | null {
  const normalizedText = text.toLocaleLowerCase();

  for (const rawSearchTerm of searchTerms) {
    const searchTerm = rawSearchTerm.trim();
    if (!searchTerm) {
      continue;
    }

    const matchIndex = normalizedText.indexOf(searchTerm.toLocaleLowerCase());
    if (matchIndex === -1) {
      continue;
    }

    const matchEnd = matchIndex + searchTerm.length;
    return [
      text.slice(0, matchIndex),
      SUPPORT_TICKET_SEARCH_HIGHLIGHT_PRE_TAG,
      text.slice(matchIndex, matchEnd),
      SUPPORT_TICKET_SEARCH_HIGHLIGHT_POST_TAG,
      text.slice(matchEnd),
    ].join("");
  }

  return null;
}

function findFirstHighlightBounds(
  segments: readonly SearchHighlightSegment[],
): { start: number; end: number } | null {
  let cursor = 0;
  for (const segment of segments) {
    const nextCursor = cursor + segment.text.length;
    if (segment.highlighted) {
      return { start: cursor, end: nextCursor };
    }
    cursor = nextCursor;
  }

  return null;
}

function getSegmentsTextLength(
  segments: readonly SearchHighlightSegment[],
): number {
  return segments.reduce((sum, segment) => sum + segment.text.length, 0);
}

function expandStartToWordBoundary(
  segments: readonly SearchHighlightSegment[],
  start: number,
): number {
  if (start <= 0) {
    return 0;
  }

  const text = segments.map((segment) => segment.text).join("");
  if (/\s/.test(text[start - 1] ?? "") || /\s/.test(text[start] ?? "")) {
    return start;
  }

  for (let index = start - 1; index >= 0; index--) {
    if (/\s/.test(text[index] ?? "")) {
      return index + 1;
    }
  }

  return 0;
}

function sliceSearchHighlightSegments(
  segments: readonly SearchHighlightSegment[],
  start: number,
  end: number,
): SearchHighlightSegment[] {
  const preview: SearchHighlightSegment[] = [];
  let cursor = 0;

  for (const segment of segments) {
    const segmentStart = cursor;
    const segmentEnd = cursor + segment.text.length;
    cursor = segmentEnd;

    if (segmentEnd <= start) {
      continue;
    }
    if (segmentStart >= end) {
      break;
    }

    preview.push({
      highlighted: segment.highlighted,
      text: segment.text.slice(
        Math.max(0, start - segmentStart),
        Math.min(segment.text.length, end - segmentStart),
      ),
    });
  }

  return preview.filter((segment) => segment.text.length > 0);
}

export {
  type SupportTicketSearchHighlight,
  type SupportTicketSearchHighlightField,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
